/**
 * widgetLayout — the TILED model, for the dashboard's widget grid.
 *
 * This is the other half of the layout story. `panelLayout` describes panes
 * that FLOAT over a canvas: a pane's width changes nothing but itself, and two
 * panes may overlap. A dashboard is the opposite — widgets TILE. Every widget
 * occupies whole columns of a 12-column grid, nothing overlaps, and widening
 * one pushes the next onto another row. The two models share the storage
 * namespace (`services/layout/layoutStore`) and nothing else, deliberately:
 * trying to make one reducer serve both is how you get a layout system that
 * does neither well.
 *
 * THE GRID. Twelve columns, but a widget's `span` is expressed in QUARTERS —
 * 1, 2, 3 or 4 — because a dashboard row divides into halves, thirds and
 * quarters and nothing finer. Four span-units fill a row (`SPAN_UNITS`), and
 * one unit is three of the twelve CSS columns (`COLUMNS_PER_SPAN`).
 *
 * ROW FLOW. `row`/`col` are stored on each slot, but they are OUTPUTS, not
 * inputs: the arrangement is a linear ORDER of widgets, and rows fall out of
 * packing that order left to right, wrapping when the next widget will not
 * fit. That is what makes hiding a widget behave — the ones after it flow up,
 * exactly as they would in a document — and it is why every operation ends in
 * `normalizeFlow`, which re-derives the stored positions from the order.
 *
 * THE LAST WIDGET IN A ROW GROWS. A tiled grid must not leave a hole: a row
 * holding one half-width widget and nothing else reads as a rendering bug, not
 * as a choice. `flowRows` gives the remaining columns to the last widget on
 * the row, so hiding one of a pair leaves the survivor full-width.
 *
 * Everything here is PURE and generic over a grid's own widget ids, so the
 * model is testable without a DOM, a store or a browser.
 */

import {
  clearScoped,
  readScoped,
  scopeKey,
  writeScoped,
  LayoutStorage,
} from '../../services/layout/layoutStore';

/** Width in quarters of a row. 4 is full width. */
export type WidgetSpan = 1 | 2 | 3 | 4;

/**
 * A widget's height preset.
 *
 * A DEFINITE height the widget fills and scrolls inside, not the floor it used
 * to be — see `HEIGHT_CLASS` in `DashboardGrid` for why a floor made the S/M/L
 * control look broken.
 */
export type WidgetHeight = 'sm' | 'md' | 'lg';

export interface WidgetSlot<Id extends string = string> {
  id: Id;
  /** Derived by `normalizeFlow`; stored so a load renders before any packing. */
  row: number;
  /** Offset within the row, in span units (0–3). Also derived. */
  col: number;
  span: WidgetSpan;
  height: WidgetHeight;
  visible: boolean;
}

export interface WidgetLayout<Id extends string = string> {
  slots: Record<Id, WidgetSlot<Id>>;
  /** The arrangement IS this order; rows are packed from it. */
  order: Id[];
}

/** One dashboard's configuration. Everything else is generic. */
export interface WidgetGrid<Id extends string = string> {
  ids: Id[];
  /** How far a widget may be narrowed. Absent means 1. */
  minSpan?: Partial<Record<Id, WidgetSpan>>;
  /**
   * How far the FLOW may widen a widget. Absent means a full row.
   *
   * Only `pack`'s "the last widget in a short row grows" rule consults this —
   * see the note there. A user who drags a card wider still gets what they
   * asked for; this is about what the layout does on its own.
   */
  maxSpan?: Partial<Record<Id, WidgetSpan>>;
  defaults: WidgetLayout<Id>;
  /** Where `layoutStore` keeps it — no `entityId`, a dashboard is per user. */
  scope: { kind: string; name: string; version?: string };
}

/** CSS columns in a row. */
export const COLUMNS = 12;

/** Span units in a row. */
export const SPAN_UNITS = 4;

/** CSS columns one span unit is worth. */
export const COLUMNS_PER_SPAN = COLUMNS / SPAN_UNITS;

export const SPANS: WidgetSpan[] = [1, 2, 3, 4];

export const HEIGHTS: WidgetHeight[] = ['sm', 'md', 'lg'];

/** Tailwind's `lg` — below it the grid is one column. Same line panes use. */
export const SINGLE_COLUMN_BELOW = 1024;

export function isSingleColumn(viewportWidth: number): boolean {
  return viewportWidth < SINGLE_COLUMN_BELOW;
}

/** CSS columns a span is worth. */
export const spanColumns = (span: WidgetSpan | number): number =>
  Math.max(1, Math.min(COLUMNS, Math.round(span * COLUMNS_PER_SPAN)));

const minSpanOf = <Id extends string>(grid: WidgetGrid<Id>, id: Id): WidgetSpan =>
  grid.minSpan?.[id] ?? 1;

const clampSpan = <Id extends string>(
  grid: WidgetGrid<Id>,
  id: Id,
  span: number
): WidgetSpan => {
  const floor = minSpanOf(grid, id);
  const rounded = Math.round(span);
  if (!Number.isFinite(rounded)) return floor;
  return Math.min(SPAN_UNITS, Math.max(floor, rounded)) as WidgetSpan;
};

/* ------------------------------------------------------------------ */
/*  Flow                                                               */
/* ------------------------------------------------------------------ */

/** A widget as it is actually rendered: its slot plus its resolved width. */
export interface PlacedWidget<Id extends string = string> {
  slot: WidgetSlot<Id>;
  /** Span after a lone widget has grown to fill its row. */
  span: WidgetSpan;
  /** `span` in CSS columns, which is what the renderer wants. */
  columns: number;
}

/**
 * The layout's widgets in arrangement order.
 *
 * `order` is the truth, but it is untrusted after a load, so anything it omits
 * falls back to the grid's declared order rather than disappearing.
 */
export function orderedIds<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>
): Id[] {
  const seen = new Set<Id>();
  const out: Id[] = [];
  (layout.order || []).forEach((id) => {
    if (grid.ids.includes(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  });
  grid.ids.forEach((id) => {
    if (!seen.has(id)) out.push(id);
  });
  return out;
}

/**
 * Pack an ordered list of spans into rows, wrapping when the next will not fit.
 *
 * The one rule beyond wrapping: a row that ends short hands its leftover
 * columns to its LAST widget, so the grid never renders a gap.
 *
 * …UP TO THAT WIDGET'S OWN CEILING. The rule is right for a list and wrong for
 * a card designed as one of a pair: when Productivity's row-mate is gated away
 * it does not want the whole row, it wants to stay half of one. A widget with a
 * `maxSpan` grows only as far as that, and the row is allowed to end short —
 * a small gap on the right reads far better than a three-number letterbox.
 */
const pack = <Id extends string>(
  slots: WidgetSlot<Id>[],
  maxSpanOf: (id: Id) => WidgetSpan = () => SPAN_UNITS as WidgetSpan
): PlacedWidget<Id>[][] => {
  const rows: PlacedWidget<Id>[][] = [];
  let current: PlacedWidget<Id>[] = [];
  let used = 0;

  slots.forEach((slot) => {
    const span = Math.min(SPAN_UNITS, Math.max(1, slot.span)) as WidgetSpan;
    if (used + span > SPAN_UNITS && current.length > 0) {
      rows.push(current);
      current = [];
      used = 0;
    }
    current.push({ slot, span, columns: spanColumns(span) });
    used += span;
  });
  if (current.length > 0) rows.push(current);

  return rows.map((row) => {
    const total = row.reduce((sum, placed) => sum + placed.span, 0);
    if (total >= SPAN_UNITS) return row;
    const remainder = SPAN_UNITS - total;
    return row.map((placed, i) => {
      if (i !== row.length - 1) return placed;
      const ceiling = maxSpanOf(placed.slot.id);
      const span = Math.min(placed.span + remainder, ceiling) as WidgetSpan;
      if (span === placed.span) return placed;
      return { ...placed, span, columns: spanColumns(span) };
    });
  });
};

/**
 * The rows a renderer draws, top to bottom.
 *
 * `visible` filters what the user has hidden; `allow` is where a widget's own
 * `requires` gate goes — a widget the licence does not cover is absent from
 * the flow entirely, so the widgets after it move up rather than leaving a
 * hole where a locked feature used to be.
 *
 * `singleColumn` is the narrow-viewport rule: below `lg` every widget is full
 * width and stacks, and — like `activeSheet` in the pane model — this is a
 * READ, never written back, so widening the window restores the arrangement.
 * (The renderer states the same rule in CSS, as `grid-cols-1 lg:grid-cols-12`;
 * this exists for consumers that measure, such as drag-and-drop.)
 */
export function flowRows<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  options: { allow?: (id: Id) => boolean; singleColumn?: boolean } = {}
): PlacedWidget<Id>[][] {
  const { allow, singleColumn = false } = options;
  const slots = orderedIds(grid, layout)
    .map((id) => layout.slots[id])
    .filter((slot): slot is WidgetSlot<Id> => !!slot && slot.visible)
    .filter((slot) => (allow ? allow(slot.id) : true));

  if (singleColumn) {
    return slots.map((slot) => [
      { slot, span: SPAN_UNITS as WidgetSpan, columns: COLUMNS },
    ]);
  }
  return pack(slots, (id) => grid.maxSpan?.[id] ?? (SPAN_UNITS as WidgetSpan));
}

/**
 * Re-derive every slot's `row`/`col` from the order.
 *
 * Hidden slots are packed too, holding their place, so unhiding one puts it
 * back where it was rather than at the bottom.
 */
export function normalizeFlow<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>
): WidgetLayout<Id> {
  const order = orderedIds(grid, layout);
  const slots = { ...layout.slots };
  let row = 0;
  let used = 0;

  order.forEach((id) => {
    const slot = slots[id];
    if (!slot) return;
    if (used + slot.span > SPAN_UNITS && used > 0) {
      row += 1;
      used = 0;
    }
    slots[id] = { ...slot, row, col: used };
    used += slot.span;
  });

  return { slots, order };
}

/* ------------------------------------------------------------------ */
/*  Operations                                                         */
/* ------------------------------------------------------------------ */

export function setWidgetVisible<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
  visible: boolean
): WidgetLayout<Id> {
  const slot = layout.slots[id];
  if (!slot || slot.visible === visible) return layout;
  return normalizeFlow(grid, {
    ...layout,
    slots: { ...layout.slots, [id]: { ...slot, visible } },
  });
}

export function toggleWidget<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  id: Id
): WidgetLayout<Id> {
  return setWidgetVisible(grid, layout, id, !layout.slots[id]?.visible);
}

/** Resize a widget, clamped to its own floor and to a full row. */
export function setWidgetSpan<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
  span: number
): WidgetLayout<Id> {
  const slot = layout.slots[id];
  if (!slot) return layout;
  const next = clampSpan(grid, id, span);
  if (next === slot.span) return layout;
  return normalizeFlow(grid, {
    ...layout,
    slots: { ...layout.slots, [id]: { ...slot, span: next } },
  });
}

export function setWidgetHeight<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
  height: WidgetHeight
): WidgetLayout<Id> {
  const slot = layout.slots[id];
  if (!slot || slot.height === height || !HEIGHTS.includes(height)) return layout;
  return { ...layout, slots: { ...layout.slots, [id]: { ...slot, height } } };
}

/**
 * Move a widget to a position in the arrangement.
 *
 * Positions are (row, col) because that is what a drop target knows; they are
 * resolved against the CURRENT flow into an index in the order, and the rows
 * are then re-derived. Dropping past the end appends.
 */
export function moveWidget<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
  row: number,
  col: number
): WidgetLayout<Id> {
  if (!layout.slots[id]) return layout;
  const order = orderedIds(grid, layout);
  const remaining = order.filter((other) => other !== id);

  // Everything that sits before the drop point keeps its place; the widget
  // lands in front of the first thing that does not.
  const before = remaining.filter((other) => {
    const slot = layout.slots[other];
    if (!slot) return false;
    if (slot.row !== row) return slot.row < row;
    return slot.col < col;
  }).length;

  const next = [...remaining];
  next.splice(Math.min(before, next.length), 0, id);
  return normalizeFlow(grid, { ...layout, order: next });
}

/**
 * Move a widget one place earlier or later in the arrangement.
 *
 * The keyboard's version of the drag. `moveWidget` cannot serve it: its
 * position is a (row, col) a DROP TARGET knows, and "one place left" is not a
 * point on the grid — it is an index in the order, which is the thing the
 * arrangement actually is.
 *
 * IT STEPS OVER ANYTHING NOT ON SCREEN, and there are two ways to be off it:
 * the user hid the widget (`visible`), or its own gate dropped it from the
 * flow this paint (`allow` — an unlicensed card, or one with no data to show).
 * Both hold their place in the order so they return to it, and both must be
 * skipped: a keypress that swaps a widget with something invisible looks
 * exactly like a keypress that did nothing, which is the bug this whole
 * control exists to not have.
 */
export function moveWidgetBy<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
  delta: number,
  options: { allow?: (id: Id) => boolean } = {}
): WidgetLayout<Id> {
  if (!layout.slots[id] || !delta) return layout;
  const { allow } = options;
  const order = orderedIds(grid, layout);
  const visible = order.filter(
    (other) => layout.slots[other]?.visible && (allow ? allow(other) : true)
  );
  const from = visible.indexOf(id);
  if (from === -1) return layout;

  const to = from + (delta > 0 ? 1 : -1);
  if (to < 0 || to >= visible.length) return layout;

  // Swap the two in the FULL order, so the hidden widgets between them keep
  // the positions they would return to if unhidden.
  const a = order.indexOf(id);
  const b = order.indexOf(visible[to]);
  const next = [...order];
  next[a] = visible[to];
  next[b] = id;
  return normalizeFlow(grid, { ...layout, order: next });
}

/** Put the grid back to how it shipped. */
export function resetLayout<Id extends string>(
  grid: WidgetGrid<Id>
): WidgetLayout<Id> {
  return normalizeFlow(grid, grid.defaults);
}

/* ------------------------------------------------------------------ */
/*  Persistence                                                        */
/* ------------------------------------------------------------------ */

/**
 * Repair anything a stored layout got wrong.
 *
 * Same contract as the pane model's `validateLayout`: storage is untrusted
 * input. It may name a widget that has since been retired, carry a span from a
 * build where spans were CSS columns, or be outright junk — all of it degrades
 * to the defaults for that field rather than rendering a broken grid.
 */
export function validateWidgetLayout<Id extends string>(
  grid: WidgetGrid<Id>,
  raw: unknown
): WidgetLayout<Id> {
  if (!raw || typeof raw !== 'object') return resetLayout(grid);
  const candidate = raw as Partial<WidgetLayout<Id>> & { slots?: any };

  const slots = {} as Record<Id, WidgetSlot<Id>>;
  grid.ids.forEach((id) => {
    const fallback = grid.defaults.slots[id];
    const stored = candidate.slots?.[id];
    slots[id] = {
      id,
      // row/col are re-derived below; a stored value only orders the load.
      row:
        typeof stored?.row === 'number' && Number.isFinite(stored.row) && stored.row >= 0
          ? Math.floor(stored.row)
          : fallback.row,
      col:
        typeof stored?.col === 'number' && Number.isFinite(stored.col) && stored.col >= 0
          ? Math.min(SPAN_UNITS - 1, Math.floor(stored.col))
          : fallback.col,
      span:
        typeof stored?.span === 'number' && Number.isFinite(stored.span)
          ? clampSpan(grid, id, stored.span)
          : fallback.span,
      height: HEIGHTS.includes(stored?.height) ? stored.height : fallback.height,
      visible: typeof stored?.visible === 'boolean' ? stored.visible : fallback.visible,
    };
  });

  /*
    An order from storage may name a widget that no longer exists and may omit
    one that has since been added. `orderedIds` drops the first and appends the
    second; a stored order that is not an array at all falls back to the
    positions the slots were saved at, which is the closest thing to what the
    user last saw.
  */
  const storedOrder = Array.isArray(candidate.order)
    ? (candidate.order as Id[])
    : grid.ids
      .slice()
      .sort((a, b) => slots[a].row - slots[b].row || slots[a].col - slots[b].col);

  return normalizeFlow(grid, { slots, order: storedOrder });
}

/** Where this grid's layout lives for one user. */
export function widgetLayoutKey<Id extends string>(
  grid: WidgetGrid<Id>,
  userId: string
): string {
  return scopeKey({ ...grid.scope, userId });
}

export function loadWidgetLayout<Id extends string>(
  grid: WidgetGrid<Id>,
  userId: string,
  storage?: LayoutStorage | null
): WidgetLayout<Id> {
  const raw = readScoped(widgetLayoutKey(grid, userId), storage);
  if (raw === null) return resetLayout(grid);
  return validateWidgetLayout(grid, raw);
}

export function saveWidgetLayout<Id extends string>(
  grid: WidgetGrid<Id>,
  layout: WidgetLayout<Id>,
  userId: string,
  storage?: LayoutStorage | null
): void {
  writeScoped(widgetLayoutKey(grid, userId), layout, storage);
}

export function clearWidgetLayout<Id extends string>(
  grid: WidgetGrid<Id>,
  userId: string,
  storage?: LayoutStorage | null
): void {
  clearScoped(widgetLayoutKey(grid, userId), storage);
}
